import { memoryStore, useStore } from "../../lib/store";
import type { CurrentUser } from "../../domains/user/hooks/useCurrentUser";
import HcpIdentifierModal from "../modals/room/HcpIdentifierModal";

type RecordOutcome = "saved" | "cancelled";

type RecordRequest = {
  // Keys the modal, so that each ask opens a fresh form: a draft or a signed
  // directory selection left by a cancel must not come back at the next ask.
  id: number;
  settle: (outcome: RecordOutcome) => void;
};

// The record modal on screen, if any. One at a time: a second ask cancels the
// first, so that no join waits for a modal that was replaced.
const recordRequestStore = memoryStore<RecordRequest | null>(null);
let nextRequestId = 0;

// Settles `request` only while it is still the one on screen: a save that was
// in flight when a second ask replaced it must not close the new modal.
function settleRequest(request: RecordRequest, outcome: RecordOutcome): void {
  if (recordRequestStore.get() !== request) return;
  recordRequestStore.set(null);
  request.settle(outcome);
}

/**
 * Whether the join of this room must first collect a professional record: the
 * room asks for one (`requireMedicalIdentifier`) and the reader has none. A
 * reader who already holds a record, or a room that does not ask, joins
 * straight away. The server refuses the join of a reader without a record
 * either way; this is what lets the join ask first.
 */
export function needsProfessionalRecord(
  room: { requireMedicalIdentifier: boolean | null },
  user: Pick<CurrentUser, "professionalRecordStatus">
): boolean {
  return (
    room.requireMedicalIdentifier === true &&
    user.professionalRecordStatus === "missing"
  );
}

/**
 * Opens the professional record modal over whatever page the reader is on and
 * resolves when they save a record or cancel. The caller joins the room on
 * `saved` and does not join on `cancelled`.
 */
export function askForProfessionalRecord(): Promise<RecordOutcome> {
  return new Promise((resolve) => {
    const previous = recordRequestStore.get();
    if (previous !== null) settleRequest(previous, "cancelled");
    nextRequestId += 1;
    recordRequestStore.set({ id: nextRequestId, settle: resolve });
  });
}

/**
 * Drops an ask still open: at logout, so that the join waiting on it ends and
 * the next account on this tab never sees the modal.
 */
export function clearProfessionalRecordRequest(): void {
  const request = recordRequestStore.get();
  if (request !== null) settleRequest(request, "cancelled");
}

/** Renders the modal `askForProfessionalRecord` opens. Mounted once, app-wide. */
export function ProfessionalRecordHost(): React.JSX.Element | null {
  const [request] = useStore(recordRequestStore);
  if (request === null) return null;
  return (
    <HcpIdentifierModal
      key={request.id}
      visible
      onSaved={() => {
        settleRequest(request, "saved");
      }}
      onCancel={() => {
        settleRequest(request, "cancelled");
      }}
    />
  );
}
